/** tm/legal/useOverviewData.ts — 개요 자료: 새 소식 5건(알림 대상과 같은 조건 · 서버 순서) + 「곧 시행」(같은 법령·같은 시행일은 한 줄, 공포번호 표시) */
import { useEffect, useState } from 'react';
import { getFeed, getUpcoming } from './legalApi';
import { kstToday } from './legalTheme';
import type { FeedRow, LegalFeedRow, UpcomingRow } from './legalTypes';

export interface SoonRow { key: string; title: string; effective_at: string; id: number; nos: string[]; promulgated: string | null }

const base = { sub: '', q: '', starred: false, lawKind: '' as const };

const fromServer = (rows: UpcomingRow[]): SoonRow[] => rows.filter(r => r.promulgations?.length).map(r => {
  const ps = [...r.promulgations].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  return { key: `${r.law}${r.effective_at}`, title: r.law, effective_at: r.effective_at, id: ps[0].id, nos: ps.map(p => (p.no || '').replace(/^제|호$/g, '')).filter(Boolean), promulgated: ps[0].date };
});

/** 서버 묶음이 없을 때 — 법령 목록에서 제목+시행일로 묶는다 (가장 최근 공포 1건으로 열기) */
function fromFeed(rows: FeedRow[]): SoonRow[] {
  const today = kstToday();
  const up = rows.filter((r): r is LegalFeedRow => r.kind === 'legal' && !!r.effective_at && r.effective_at.slice(0, 10) > today && !r.meta?.minor);
  const map = new Map<string, SoonRow>();
  for (const r of [...up].sort((a, b) => (b.decided_at || '').localeCompare(a.decided_at || ''))) {
    const eff = (r.effective_at || '').slice(0, 10);
    const k = `${r.title}${eff}`;
    const no = (r.meta?.promulgationNo || r.case_no || '').replace(/^제|호$/g, '');
    const cur = map.get(k);
    if (cur) { if (no && !cur.nos.includes(no)) cur.nos.push(no); continue; }
    map.set(k, { key: k, title: r.title, effective_at: eff, id: r.id, nos: no ? [no] : [], promulgated: r.decided_at });
  }
  return Array.from(map.values());
}

export function useOverviewData(label: string, enabled: boolean) {
  const [fresh, setFresh] = useState<FeedRow[] | null>(null);
  const [soon, setSoon] = useState<SoonRow[] | null>(null);
  useEffect(() => {
    let on = true; setFresh(null); setSoon(null);
    if (!enabled) return undefined;
    getFeed({ ...base, topic: label, tab: 'all', onlyNew: true }, 1, 5).then(d => { if (on) setFresh(d?.rows || []); }, () => on && setFresh([]));
    const viaFeed = () => getFeed({ ...base, topic: label, tab: 'law', onlyNew: false }, 1, 60).then(d => fromFeed(d?.rows || []), () => []);
    (label ? getUpcoming(label) : Promise.resolve(null))
      .then(r => (r && r.length ? fromServer(r) : viaFeed()))
      .then(list => { if (on) setSoon(list.sort((a, b) => a.effective_at.localeCompare(b.effective_at)).slice(0, 5)); });
    return () => { on = false; };
  }, [label, enabled]);
  return { fresh, soon };
}
